import { Component, ElementRef, ViewChild, ViewContainerRef, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogModule, DialogService } from '@progress/kendo-angular-dialog';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { NotificationService } from '@progress/kendo-angular-notification';
import { infoCircleIcon } from '@progress/kendo-svg-icons';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { getApiErrorBodyMessage, getApiErrorCode, getApiErrorStatus } from '../../../../../../../core/models/api-error';
import { environment } from '../../../../../../../../environments/environment';
import { TicketingEmailForm, TicketingEmailFormService } from './service/ticketing-form.service';
import {
  PRIMARY_TICKETING_EMAIL_EXISTS,
  TicketingEmailListResponse,
  TicketingEmailResponse,
  TicketingEmailService,
} from './service/ticketing-email.service';
import DataTable from 'datatables.net-dt';

const PRIMARY_EXISTS_FALLBACK_MESSAGE = "An organization can't have more than one primary ticketing email.";
const EMAIL_NOT_FOUND_MESSAGE = 'This email no longer exists';

@Component({
  selector: 'app-ticketing',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    IconsModule,
    InputsModule,
    LabelModule,
    ButtonsModule,
    DialogModule,
    DropDownListModule,
    ReactiveFormsModule,
    DatatableComponent,
  ],
  templateUrl: './ticketing.component.html',
  styleUrl: './ticketing.component.scss',
})
export class TicketingComponent extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public gridService = inject(TicketingEmailService);
  public route = inject(ActivatedRoute);
  private formService = inject(TicketingEmailFormService);
  private notificationService = inject(NotificationService);
  private dialogService = inject(DialogService);
  private viewContainerRef = inject(ViewContainerRef);
  private elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  private baseUrl = `${environment.apiUrl}/v1/organization/administrative/ticketingemail`;
  public id!: number;

  public infoCircleIcon: SVGIcon = infoCircleIcon;

  public datatableUrl = signal<string>('');
  public formGroup = signal<FormGroup<TicketingEmailForm> | undefined>(undefined);
  public isNew = signal<boolean>(false);
  /** True while any write (dialog save or row action) is in flight. */
  public isSaving = signal<boolean>(false);
  /**
   * Primaries in the whole org, as reported by the backend (not the visible
   * rows). null until a list response with primary_count arrives, so the UI
   * never offers the primary checkbox based on a count it doesn't know.
   */
  public primaryCount = signal<number | null>(null);
  public hasMultiplePrimaries = computed(() => (this.primaryCount() ?? 0) > 1);
  /** Backend message for a rejected primary, shown inline in the dialog. */
  public primaryError = signal<string | null>(null);
  // Rows of the page currently shown, keyed by pk, so row actions can look up
  // the email they act on.
  private rows = new Map<string, TicketingEmailResponse>();

  private originalEmail: string | null = null;

  public isPrimaryOptions = [
    { text: 'Yes', value: true },
    { text: 'No', value: false },
  ];

  private isPrimaryFilter: boolean | null = null;

  public initialFilters = this.buildFilters();

  public datatableColumns = [
    { data: 'email', name: 'email', title: 'Email', type: 'string', render: DataTable.render.text() },
    {
      data: 'is_primary',
      name: 'is_primary',
      title: 'Primary',
      type: 'boolean',
      render: (data: boolean) => (data ? '<span class="badge text-bg-primary">Primary</span>' : 'Alternate'),
    },
    {
      data: null,
      name: 'actions',
      title: 'Action',
      type: 'actions',
      orderable: false,
      render: (_data: unknown, _type: unknown, row: TicketingEmailResponse) => {
        const pk = escapeHtml(String(row.pk));
        const disabled = this.isSaving() ? 'disabled' : '';
        const primaryAction = row.is_primary
          ? `<button type="button" class="cyflare-btn-secondary cyflare-btn-sm ticketing-row-action ticketing-primary-action action-unmark-primary" data-pk="${pk}" ${disabled}>
              Unmark primary
            </button>`
          : `<button type="button" class="cyflare-btn-secondary cyflare-btn-sm ticketing-row-action ticketing-primary-action action-make-primary" data-pk="${pk}" ${disabled}>
              Make primary
            </button>`;
        return `
          <div class="d-flex gap-2">
            <button type="button" class="cyflare-btn-secondary cyflare-btn-sm ticketing-row-action action-edit" data-pk="${pk}" ${disabled}>
              Edit
            </button>
            ${primaryAction}
            <button type="button" class="cyflare-btn-danger cyflare-btn-sm ticketing-row-action action-remove" data-pk="${pk}" ${disabled}>
              Remove
            </button>
          </div>
        `;
      },
    },
  ];

  constructor() {
    super();
    // Row buttons are plain HTML rendered by DataTables, so their disabled
    // state is kept in sync with the in-flight flag by hand.
    effect(() => {
      const busy = this.isSaving();
      this.elementRef.nativeElement
        .querySelectorAll<HTMLButtonElement>('.ticketing-row-action')
        .forEach((button) => (button.disabled = busy));
    });
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.id = parseInt(params['organizationId'], 10);
      // Still the source of truth for update and delete, which build their
      // endpoint from it, even though the table no longer reads through the
      // service.
      this.gridService.url = `${this.baseUrl}(${this.id})/`;
      this.rows.clear();
      this.primaryCount.set(null);
      this.datatableUrl.set(this.gridService.url);
    });
  }

  /** Called by the datatable with the raw list response after every load. */
  public onListLoaded = (response: TicketingEmailListResponse): void => {
    this.rows = new Map((response.value ?? []).map((row) => [String(row.pk), row]));
    // A missing or malformed count is treated as unknown, not as "no primary".
    const primaryCount = response.primary_count;
    this.primaryCount.set(typeof primaryCount === 'number' ? primaryCount : null);
    const form = this.formGroup();
    if (form) {
      this.applyPrimaryAvailability(form);
    }
  };

  private buildFilters(): Record<string, string> {
    const filters: Record<string, string> = {};
    if (this.isPrimaryFilter != null) {
      filters['$filter'] = `is_primary eq ${this.isPrimaryFilter}`;
    }
    return filters;
  }

  public isPrimaryChange($event: { text: string; value: boolean | null }): void {
    this.isPrimaryFilter = $event?.value ?? null;
    this.table?.setFilters(this.buildFilters());
  }

  public onTableClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest('button');
    // Every action needs the row it belongs to; an unknown pk would otherwise
    // open the editor in "Add" mode.
    const row = this.rows.get(button?.getAttribute('data-pk') ?? '');
    if (!button || !row || this.isSaving()) {
      return;
    }

    if (button.classList.contains('action-edit')) {
      this.openEditor(row);
    } else if (button.classList.contains('action-make-primary')) {
      this.confirmMakePrimary(row);
    } else if (button.classList.contains('action-unmark-primary')) {
      this.runRowAction(this.gridService.updateEmail({ pk: row.pk, is_primary: false }));
    } else if (button.classList.contains('action-remove')) {
      this.runRowAction(this.gridService.deleteEmail(row.pk));
    }
  }

  public openEditor(email?: TicketingEmailResponse): void {
    this.isNew.set(!email);
    this.originalEmail = email ? email.email : null;
    this.primaryError.set(null);
    // The form models pk as a string (TicketingEmail) while the API returns a
    // number, so the row is adapted instead of passed through.
    const form = this.formService.createTicketingEmailForm(
      email ? { pk: String(email.pk), email: email.email, is_primary: email.is_primary } : undefined
    );
    this.applyPrimaryAvailability(form);
    this.formGroup.set(form);
  }

  public closeEditor(): void {
    // The dialog close button and the backdrop both land here; ignore them
    // while a save is in flight so the user cannot walk away mid-request.
    if (this.isSaving()) {
      return;
    }
    this.formGroup.set(undefined);
    this.originalEmail = null;
    this.primaryError.set(null);
  }

  /** New rows are always savable; existing ones only once the email actually changed. */
  public hasFormChanged(): boolean {
    const formValue = this.formGroup()?.value;
    if (!formValue || this.isNew()) {
      return true;
    }
    return formValue.email !== this.originalEmail;
  }

  public save(): void {
    const formGroup = this.formGroup();
    if (!formGroup || formGroup.invalid || this.isSaving()) {
      return;
    }
    // A disabled is_primary control is left out of value, so it reads as
    // undefined here and the email is created as an alternate.
    const formValue = formGroup.value;

    // create and update resolve to different payload types; the caller only
    // cares that the request completed, so the union is widened here.
    // Updates only edit the address: primary is changed through the row actions.
    const request: Observable<unknown> = this.isNew()
      ? this.gridService.createEmail({
          organization_id: this.id,
          ticketing_email: {
            pk: formValue.pk ?? undefined,
            email: formValue.email!,
            is_primary: formValue.is_primary ?? false,
          },
        })
      : this.gridService.updateEmail({
          pk: Number(formValue.pk),
          email: formValue.email!,
        });

    this.primaryError.set(null);
    this.isSaving.set(true);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeEditor();
        this.table?.reload();
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error(error);
        this.handleWriteError(error, true);
      },
    });
  }

  /**
   * The primary checkbox is only offered when adding an email to an org known
   * to have no primary. Otherwise (including an unknown count) the control is
   * disabled, which keeps is_primary out of the form value.
   */
  private applyPrimaryAvailability(form: FormGroup<TicketingEmailForm>): void {
    const control = form.controls.is_primary;
    if (this.isNew() && this.primaryCount() === 0) {
      control.enable();
    } else {
      control.setValue(false);
      control.disable();
    }
  }

  private confirmMakePrimary(row: TicketingEmailResponse): void {
    const primaryCount = this.primaryCount();
    if (primaryCount === 0) {
      this.runRowAction(this.gridService.makePrimary(row.pk));
      return;
    }

    const dialog = this.dialogService.open({
      title: 'Confirm Primary Email',
      content: `Make ${row.email} the primary ticketing email? It will replace ${this.describeCurrentPrimary(primaryCount)}.`,
      actions: [
        { text: 'No', primary: false },
        { text: 'Yes', primary: true },
      ],
      width: 450,
      appendTo: this.viewContainerRef,
    });

    dialog.result.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result instanceof DialogCloseResult) {
        return;
      }
      if ((result as { text: string }).text === 'Yes') {
        this.runRowAction(this.gridService.makePrimary(row.pk));
      }
    });
  }

  private describeCurrentPrimary(primaryCount: number | null): string {
    if (primaryCount === null) {
      // Unknown count: there may be none, so the wording stays neutral.
      return 'any current primary';
    }
    if (primaryCount > 1) {
      return 'the current primaries';
    }
    // The current primary can only be named when it is on the page shown
    // (paging or the Primary filter may hide it).
    const visiblePrimary = [...this.rows.values()].find((r) => r.is_primary);
    return visiblePrimary?.email ?? 'the current primary';
  }

  private runRowAction(request: Observable<unknown>): void {
    if (this.isSaving()) {
      return;
    }
    this.isSaving.set(true);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.table?.reload();
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error(error);
        this.handleWriteError(error, false);
      },
    });
  }

  /**
   * The interceptor skips its toast for the two cases handled here (see
   * TicketingEmailService); any other error was already reported by it.
   */
  private handleWriteError(error: unknown, fromDialog: boolean): void {
    const status = getApiErrorStatus(error);

    if (status === 400 && getApiErrorCode(error) === PRIMARY_TICKETING_EMAIL_EXISTS) {
      // Someone else set a primary since the list was loaded. Nothing was
      // saved; the reload refreshes primary_count, which hides the checkbox.
      const message = getApiErrorBodyMessage(error) ?? PRIMARY_EXISTS_FALLBACK_MESSAGE;
      if (fromDialog && this.formGroup()) {
        this.primaryError.set(message);
      } else {
        this.showError(message);
      }
      this.table?.reload();
      return;
    }

    if (status === 404) {
      this.closeEditor();
      this.showError(EMAIL_NOT_FOUND_MESSAGE);
      this.table?.reload();
    }
  }

  private showError(message: string): void {
    this.notificationService.show({
      content: message,
      cssClass: 'notification-error',
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true },
      closable: true,
      hideAfter: 5000,
    });
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
